import { addDays, daysBetween, startOfDay, startOfWeek } from './date.js'
import { isWeekAchieved, type WeekSummary } from './goals.js'
import type { Activity, ActivityType } from './types.js'

/**
 * L'observation personnalisée affichée en haut de l'accueil.
 *
 * Principe du "miroir" : on ne célèbre pas ce que l'utilisateur a fait
 * ("3 séances"), on lui dit qui il est ("tu es du genre à tenir dans la
 * durée"). C'est ce qui rend une app mémorable, et ce qui donne envie de
 * partager — le partage devient de l'expression de soi, pas de la publicité.
 *
 * Une seule observation s'affiche : la plus forte applicable.
 */
export type InsightKind =
  | 'welcome'
  | 'first_session'
  | 'comeback'
  | 'goal_reached'
  | 'consecutive_days'
  | 'best_streak'
  | 'better_than_last_week'
  | 'preferred_moment'
  | 'preferred_day'
  | 'favorite_activity'
  | 'steady'
  | 'keep_going'

export interface Insight {
  kind: InsightKind
  vars: Record<string, string | number>
}

export interface InsightContext {
  activities: readonly Activity[]
  summaries: readonly WeekSummary[]
  weeklyGoalMin: number
  currentStreak: number
  bestStreak: number
  now?: Date
}

const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

/** Plus longue série de jours consécutifs actifs, en remontant depuis aujourd'hui. */
function consecutiveDays(activities: readonly Activity[], now: Date): number {
  const days = new Set(
    activities.map((a) => startOfDay(new Date(a.startedAt)).getTime()),
  )
  const today = startOfDay(now)
  // On tolère que la série s'arrête hier : la journée en cours n'est pas finie.
  let cursor = days.has(today.getTime()) ? today : addDays(today, -1)
  let run = 0
  while (days.has(cursor.getTime())) {
    run += 1
    cursor = addDays(cursor, -1)
  }
  return run
}

function mostFrequent<T>(values: readonly T[]): { value: T; count: number } | null {
  if (values.length === 0) return null
  const tally = new Map<T, number>()
  for (const value of values) tally.set(value, (tally.get(value) ?? 0) + 1)
  let best: { value: T; count: number } | null = null
  for (const [value, count] of tally) {
    if (!best || count > best.count) best = { value, count }
  }
  return best
}

export function pickInsight(context: InsightContext): Insight {
  const now = context.now ?? new Date()
  const { activities, summaries, weeklyGoalMin } = context
  const total = activities.length

  if (total === 0) return { kind: 'welcome', vars: {} }

  const sorted = [...activities].sort((a, b) =>
    a.startedAt < b.startedAt ? -1 : 1,
  )
  const last = sorted[sorted.length - 1]!
  const week = summaries[summaries.length - 1]
  const previous = summaries[summaries.length - 2]

  if (total === 1) return { kind: 'first_session', vars: {} }

  // Reprise après une vraie pause : le moment le plus fragile, donc le plus
  // important à reconnaître.
  const beforeLast = sorted[sorted.length - 2]
  if (beforeLast) {
    const gap = daysBetween(new Date(beforeLast.startedAt), new Date(last.startedAt))
    const sinceLast = daysBetween(new Date(last.startedAt), now)
    if (gap >= 10 && sinceLast <= 3) {
      return { kind: 'comeback', vars: { days: Math.round(gap) } }
    }
  }

  if (week && isWeekAchieved(week, weeklyGoalMin)) {
    return { kind: 'goal_reached', vars: { minutes: week.totalMinutes } }
  }

  const run = consecutiveDays(activities, now)
  if (run >= 3) return { kind: 'consecutive_days', vars: { count: run } }

  if (context.currentStreak >= 2 && context.currentStreak >= context.bestStreak) {
    return { kind: 'best_streak', vars: { count: context.currentStreak } }
  }

  if (week && previous && previous.totalMinutes > 0 && week.totalMinutes > previous.totalMinutes) {
    return {
      kind: 'better_than_last_week',
      vars: { minutes: week.totalMinutes - previous.totalMinutes },
    }
  }

  if (total >= 5) {
    const mornings = activities.filter(
      (a) => new Date(a.startedAt).getHours() < 12,
    ).length
    const ratio = mornings / total
    if (ratio >= 0.65) return { kind: 'preferred_moment', vars: { moment: 'morning' } }
    if (ratio <= 0.2) return { kind: 'preferred_moment', vars: { moment: 'evening' } }
  }

  if (total >= 6) {
    const day = mostFrequent(
      activities.map((a) => WEEKDAYS[new Date(a.startedAt).getDay()]!),
    )
    if (day && day.count / total >= 0.35) {
      return { kind: 'preferred_day', vars: { day: day.value } }
    }
  }

  if (total >= 4) {
    const type = mostFrequent(activities.map((a) => a.type))
    if (type && type.count / total >= 0.5) {
      return { kind: 'favorite_activity', vars: { activity: type.value as ActivityType } }
    }
  }

  const recent = summaries.slice(-4)
  const active = recent.filter((summary) => summary.sessions > 0).length
  if (recent.length === 4 && active >= 3) {
    return { kind: 'steady', vars: { count: active } }
  }

  return { kind: 'keep_going', vars: {} }
}

/**
 * Bilan de fin de séance : le "end" de la règle pic-fin. L'app ne doit jamais
 * se terminer sur rien — on referme toujours sur ce qui vient d'être accompli.
 */
export interface SessionSummary {
  minutes: number
  /** Minutes restantes avant l'objectif de la semaine (0 si atteint) */
  remainingToGoal: number
  goalReached: boolean
  sessionsThisWeek: number
  /** Première séance de la journée ? */
  firstToday: boolean
}

export function summarizeSession(
  activities: readonly Activity[],
  weeklyGoalMin: number,
  now: Date = new Date(),
): SessionSummary | null {
  const sorted = [...activities].sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1))
  const last = sorted[0]
  if (!last) return null

  const weekStart = startOfWeek(now)
  const weekEnd = addDays(weekStart, 7)
  const thisWeek = activities.filter((activity) => {
    const date = new Date(activity.startedAt)
    return date >= weekStart && date < weekEnd
  })
  const minutes = thisWeek.reduce((sum, a) => sum + a.durationMin, 0)

  const today = startOfDay(now).getTime()
  const todayCount = activities.filter(
    (a) => startOfDay(new Date(a.startedAt)).getTime() === today,
  ).length

  return {
    minutes: last.durationMin,
    remainingToGoal: Math.max(0, weeklyGoalMin - minutes),
    goalReached: minutes >= weeklyGoalMin,
    sessionsThisWeek: thisWeek.length,
    firstToday: todayCount <= 1,
  }
}
